/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   featureSwitches.ts
 *     Description     :   TanStack Query client for the spec 044 feature switches (contracts/api.md
 *                         §1–§3): the list every page's header shows, re-read every 15 s (research
 *                         R-003); one switch's history; the Administrator's change, after which the
 *                         list and that switch's history are re-read at once; and a fail-closed reader
 *                         of one switch for a control that only shows while it is on (the stage Skip);
 *                         and the environment this console serves, from the database's dev rule.
 *     Module          :   044-visible-feature-switches (frontend/api)
 *     Component Type  :   API
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-10-25      Peter Dobler        Initial creation (spec 044 T014, T033, T055)
 *     1.1         2026-10-26      Peter Dobler        useConsoleEnvironment (operator: the prod DataMave
 *                                                     Sync page asked to type "dev")
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   @tanstack/react-query
 *     Internal        :   auth/token migrationControl feature-switches
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     SWITCHES_POLL_MS SwitchChangeError useSwitches useSwitchHistory changeSwitch
 *     useChangeSwitch isSwitchOn useSwitchOn ConsoleEnvironment useConsoleEnvironment
 *
 * =========================================================================================
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { getAccessToken } from "../auth/token.js";
import { fetchWithAuth } from "./migrationControl.js";
import type {
  FeatureSwitchKey,
  SwitchChangeRequest,
  SwitchErrorCode,
  SwitchHistoryResponse,
  SwitchListResponse,
} from "@nextgenmigration/shared/feature-switches";

const CP_URL = (import.meta.env["VITE_CONTROL_PLANE_URL"] as string | undefined) ?? "";
const base = `${CP_URL}/api/v1/switches`;

/** The header's re-read: 15 s keeps FR-005's 30 s bound with one missed poll (research R-003). */
export const SWITCHES_POLL_MS = 15_000;

const LIST_KEY = ["feature-switches", "list"] as const;
const historyKey = (key: FeatureSwitchKey) => ["feature-switches", "history", key] as const;

/** A change the server refused or could not make. `code` is null when the answer named none. */
export class SwitchChangeError extends Error {
  public readonly status: number;
  public readonly code: SwitchErrorCode | null;
  public constructor(status: number, code: SwitchErrorCode | null, message: string) {
    super(message);
    this.name = "SwitchChangeError";
    this.status = status;
    this.code = code;
  }
}

/** The codes a PUT answer can carry (contracts §3), the ones this client tells apart. */
const CHANGE_CODES: readonly SwitchErrorCode[] = [
  "INVALID_REQUEST",
  "UNKNOWN_SWITCH",
  "STALE_SWITCH",
  "TYPED_NAME_MISMATCH",
  "REASON_REQUIRED",
  "NO_CHANGE",
  "VALUE_NOT_ALLOWED",
  "INVALID_VALUE",
  "DEV_ONLY_SWITCH",
  "SWITCHES_UNAVAILABLE",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The refusal's code and words. The route answers `{ code, detail? }`; requireAdmin's 403 answers the shared envelope. */
async function refusalOf(response: Response): Promise<{ code: SwitchErrorCode | null; detail: string | null }> {
  if (response.status === 403) return { code: "FORBIDDEN", detail: null };
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON (a proxy's page, an empty body): the status alone is what is known.
  }
  const raw = isRecord(body) ? (typeof body["code"] === "string" ? body["code"] : isRecord(body["error"]) ? body["error"]["code"] : null) : null;
  const code = CHANGE_CODES.find((c) => c === raw) ?? null;
  const detail = isRecord(body) && typeof body["detail"] === "string" ? body["detail"] : null;
  return { code, detail };
}

/**
 * True when a 200's body has the list's shape in the fields a state is read from: the dev rule, and each
 * switch's key, name, state and dev-only mark; and a "not built" list. Anything else is not a state.
 */
function isSwitchList(body: unknown): body is SwitchListResponse {
  if (!isRecord(body) || typeof body["devTestAidsAllowed"] !== "boolean") return false;
  const switches = body["switches"];
  if (!Array.isArray(switches) || !Array.isArray(body["notBuilt"])) return false;
  return switches.every(
    (s: unknown) =>
      isRecord(s) &&
      typeof s["key"] === "string" &&
      typeof s["displayName"] === "string" &&
      typeof s["on"] === "boolean" &&
      typeof s["devOnly"] === "boolean",
  );
}

async function fetchSwitchList(): Promise<SwitchListResponse> {
  const body = await fetchWithAuth<unknown>(base);
  // A body that is not a switch list (a proxy's answer, a wrong route) is a store that cannot be read:
  // it fails the query, so the header says unknown and no switch reads on (FR-006).
  if (!isSwitchList(body)) throw new Error("The switch list answer is not a switch list.");
  return body;
}

/** Every switch, its effective state and the "not built" items (contracts §1), re-read every 15 s. */
export function useSwitches(): UseQueryResult<SwitchListResponse> {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: fetchSwitchList,
    refetchInterval: SWITCHES_POLL_MS,
  });
}

/** One switch's history, newest first (contracts §2). Nothing is asked while `key` is null. */
export function useSwitchHistory(key: FeatureSwitchKey | null): UseQueryResult<SwitchHistoryResponse> {
  return useQuery({
    queryKey: key === null ? ["feature-switches", "history", "none"] : historyKey(key),
    queryFn: () => fetchWithAuth<SwitchHistoryResponse>(`${base}/${encodeURIComponent(key ?? "")}/history`),
    enabled: key !== null,
  });
}

/** Change one switch (Administrators, contracts §3). Resolves when it is changed; any refusal throws a {@link SwitchChangeError}. */
export async function changeSwitch(key: FeatureSwitchKey, request: SwitchChangeRequest): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${base}/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  if (response.ok) return;
  const { code, detail } = await refusalOf(response);
  throw new SwitchChangeError(
    response.status,
    code,
    detail ?? `The switch could not be changed (${String(response.status)}${code ? ` ${code}` : ""}).`,
  );
}

export interface SwitchChangeVariables {
  readonly key: FeatureSwitchKey;
  readonly request: SwitchChangeRequest;
}

/**
 * The change as a mutation. After a change the list and that switch's history are re-read at once, so
 * the Administrator's own header shows it without waiting for the poll. After a 409 STALE_SWITCH the
 * list is re-read too: the user is asked to look again at the state as it now is.
 */
export function useChangeSwitch(): UseMutationResult<void, SwitchChangeError, SwitchChangeVariables> {
  const client = useQueryClient();
  return useMutation<void, SwitchChangeError, SwitchChangeVariables>({
    mutationFn: ({ key, request }) => changeSwitch(key, request),
    onSuccess: async (_data, { key }) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: LIST_KEY, exact: true }),
        client.invalidateQueries({ queryKey: historyKey(key), exact: true }),
      ]);
    },
    onError: async (error, { key }) => {
      if (error.code !== "STALE_SWITCH") return;
      await Promise.all([
        client.invalidateQueries({ queryKey: LIST_KEY, exact: true }),
        client.invalidateQueries({ queryKey: historyKey(key), exact: true }),
      ]);
    },
  });
}

/** True only when the list holds the switch and it is on: a missing row or no list reads off. */
export function isSwitchOn(list: SwitchListResponse | undefined, key: FeatureSwitchKey): boolean {
  return list?.switches.find((s) => s.key === key)?.on === true;
}

/**
 * One switch for a control that shows only while it is on. Fails closed: while the list is unread, and
 * once a read has failed (the earlier answer is not trusted), it reads off (FR-006).
 */
export function useSwitchOn(key: FeatureSwitchKey): boolean {
  const switches = useSwitches();
  return !switches.isError && isSwitchOn(switches.data, key);
}

/** The two environments a console is deployed to. */
export type ConsoleEnvironment = "dev" | "prod";

/**
 * The environment this console serves, from the database's one dev rule (`devTestAidsAllowed`, which the
 * control plane reads from `fn_MigrationControlIsDevServer` over the MigrationControlDB it is connected
 * to). `null` while the list is unread, once a read has failed, and when the list holds no switch (the
 * flag is then the backend's fail-closed `false`, not a reading, `db/featureSwitches.ts`): a caller then
 * offers nothing rather than guess an environment.
 */
export function useConsoleEnvironment(): ConsoleEnvironment | null {
  const switches = useSwitches();
  if (switches.isError || switches.data === undefined || switches.data.switches.length === 0) return null;
  return switches.data.devTestAidsAllowed ? "dev" : "prod";
}
